// Invite tenant user — drawer with form.
//
// Mirrors InviteUserDrawer but:
//   - No role picker — V1 = always tenant_admin.
//   - No region picker — tenant_users inherit the tenant's region.
//   - No domain allowlist — external customers, any email is fine.
//   - Locale defaults to the admin's current UI lang (en/fr) so the
//     invite email renders in their language. The new user can switch
//     after sign-in.

import { useState, type FormEvent } from 'react';
import {
  Alert,
  Button,
  Drawer,
  Spinner,
  TextInput,
} from '@gc-coach/design-system';
import type { TenantId } from '@gc-coach/shared';
import { inviteTenantUser } from '../api/endpoints.js';
import { useI18n } from '../i18n/index.js';
import styles from './InviteTenantUserDrawer.module.css';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export interface InviteTenantUserDrawerProps {
  tenantId: TenantId;
  onClose: () => void;
  onInvited: () => void;
}

export function InviteTenantUserDrawer({
  tenantId,
  onClose,
  onInvited,
}: InviteTenantUserDrawerProps) {
  const { t, lang } = useI18n();

  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [topError, setTopError] = useState<string | null>(null);

  function validate(): Record<string, string> {
    const errs: Record<string, string> = {};
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) errs['email'] = t('tenantUsers.inviteErrorRequired');
    else if (!EMAIL_RE.test(trimmed)) errs['email'] = t('tenantUsers.inviteErrorEmailFormat');
    if (!firstName.trim()) errs['firstName'] = t('tenantUsers.inviteErrorRequired');
    if (!lastName.trim()) errs['lastName'] = t('tenantUsers.inviteErrorRequired');
    return errs;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const errs = validate();
    setFieldErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setSubmitting(true);
    setTopError(null);
    const result = await inviteTenantUser(tenantId, {
      email: email.trim().toLowerCase(),
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      locale: lang === 'FR' ? 'fr' : 'en',
    });
    setSubmitting(false);

    if (result.ok) {
      onInvited();
      return;
    }
    if (result.code === 'BAD_REQUEST' && /already exists/i.test(result.message)) {
      setFieldErrors({ email: t('tenantUsers.inviteErrorDuplicate') });
      return;
    }
    if (result.code === 'BAD_REQUEST' && /not active/i.test(result.message)) {
      setTopError(t('tenantUsers.inviteErrorTenantInactive'));
      return;
    }
    setTopError(t('tenantUsers.inviteErrorGeneric'));
  }

  return (
    <Drawer
      open
      onClose={submitting ? () => undefined : onClose}
      title={t('tenantUsers.inviteTitle')}
      footer={
        <div className={styles.footer}>
          <Button kind="ghost" onClick={onClose} disabled={submitting}>
            {t('tenantUsers.cancel')}
          </Button>
          <Button
            kind="primary"
            type="submit"
            form="invite-tenant-user-form"
            disabled={submitting}
          >
            {submitting ? <Spinner /> : t('tenantUsers.sendInvite')}
          </Button>
        </div>
      }
    >
      <form id="invite-tenant-user-form" className={styles.form} onSubmit={onSubmit} noValidate>
        <p className={styles.intro}>{t('tenantUsers.inviteIntro')}</p>

        {topError && (
          <div className={styles.alert}>
            <Alert severity="danger">{topError}</Alert>
          </div>
        )}

        <TextInput
          label={t('tenantUsers.fieldEmail')}
          type="email"
          placeholder={t('tenantUsers.fieldEmailPlaceholder')}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          errorMessage={fieldErrors['email']}
          autoComplete="off"
          required
        />

        <div className={styles.row2}>
          <TextInput
            label={t('tenantUsers.fieldFirstName')}
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            errorMessage={fieldErrors['firstName']}
            autoComplete="given-name"
            required
          />
          <TextInput
            label={t('tenantUsers.fieldLastName')}
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            errorMessage={fieldErrors['lastName']}
            autoComplete="family-name"
            required
          />
        </div>

        <div>
          <span className={styles.legend}>{t('tenantUsers.fieldRole')}</span>
          <div className={styles.regionLocked}>{t('tenantUsers.roleTenantAdminLocked')}</div>
        </div>
      </form>
    </Drawer>
  );
}
